import React from "react";
import { Clock, Target, Award, ArrowUp } from "lucide-react";

const PerformanceTrendSection = ({ performanceData }) => {
  const accuracyData = performanceData?.trendData?.accuracy || [];
  const percentageData = performanceData?.trendData?.percentage || [];
  const timeData = performanceData?.trendData?.timePerQuestion || [];
  const labels = performanceData?.trendData?.labels || [];

  const maxAccuracy = Math.max(...accuracyData, 1);
  const maxPercentage = Math.max(...percentageData, 1);
  const maxTime = Math.max(...timeData, 1);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-gray-900">Performance Trend</h2>
        </div>

        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
          <ArrowUp className="w-3 h-3 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700">
            Improving
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg text-center">
          <Target className="w-4 h-4 text-blue-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">
            +{performanceData.metrics?.accuracyChange}%
          </div>
          <div className="text-[11px] text-gray-600">Accuracy</div>
        </div>

        <div className="p-2.5 bg-purple-50 border border-purple-100 rounded-lg text-center">
          <Award className="w-4 h-4 text-purple-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">
            +{performanceData.metrics?.percentageChange}%
          </div>
          <div className="text-[11px] text-gray-600">Score</div>
        </div>

        <div className="p-2.5 bg-orange-50 border border-orange-100 rounded-lg text-center">
          <Clock className="w-4 h-4 text-orange-600 mx-auto mb-1" />
          <div className="text-base font-bold text-gray-900">
            {performanceData.metrics?.timeChange}%
          </div>
          <div className="text-[11px] text-gray-600">Time</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Accuracy vs Score */}
        <div className="border border-gray-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Accuracy vs Score
            </h3>

            <div className="flex gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span>Accuracy</span>
              </div>

              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                <span>Score</span>
              </div>
            </div>
          </div>

          <div className="flex items-end justify-between h-28 gap-2">
            {labels.map((label, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="flex items-end gap-1 h-24 w-full">
                  <div
                    className="flex-1 bg-blue-500 rounded-t transition-all duration-500"
                    style={{
                      height: `${
                        ((accuracyData[index] || 0) / maxAccuracy) * 100
                      }%`,
                    }}
                  />

                  <div
                    className="flex-1 bg-purple-500 rounded-t transition-all duration-500"
                    style={{
                      height: `${
                        ((percentageData[index] || 0) / maxPercentage) * 100
                      }%`,
                    }}
                  />
                </div>

                <span className="text-[10px] text-gray-500 mt-1">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Time per Question */}
        <div className="border border-gray-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Time / Question
            </h3>

            <span className="text-xs font-semibold text-orange-600">
              {timeData[timeData.length - 1]}s
            </span>
          </div>

          <div className="flex items-end justify-between h-28 gap-2">
            {timeData.map((value, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-orange-500 rounded-t transition-all duration-500"
                  style={{
                    height: `${(value / maxTime) * 96}px`,
                  }}
                />

                <span className="text-[10px] text-gray-500 mt-1">
                  {labels[index]}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insight */}
      <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
        <div className="text-sm font-medium text-emerald-900">
          {performanceData.title}
        </div>

        <p className="text-xs text-emerald-700 mt-1">
          {performanceData.description}
        </p>
      </div>
    </div>
  );
};

export default PerformanceTrendSection;
